import { usePermisos } from "../../context/PermisosContext";
// src/components/Configuraciones/Firmware.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FEATURE_BITS as FB } from "../../constants/permisos";

// Contenidos (viven en src/components/Firmware/*)
import FlashearUSB from "../Firmware/FlashearUSB";
import ConfigurarDispositivo from "../Firmware/ConfigurarDispositivo";
import ActualizarOTA from "../Firmware/ActualizarOTA";
import { isSuperAdmin } from "../../services/authUtils";

/* ===== Helper permisos: mira un bit (1-based) del bitset en localStorage ===== */
const has = (key, currentBitset) => {
  try {
    const bitset = currentBitset ?? localStorage.getItem("endpoint") ?? "";
    const pos = FB[key];
    if (typeof pos !== "number") return undefined; // clave no definida => opcional
    const i = pos - 1;
    return i >= 0 && i < bitset.length && bitset[i] === "1";
  } catch {
    return undefined;
  }
};

/* ===== Definición de pestañas (id + etiqueta + bit de vista) ===== */
const ALL_TABS = [
  { id: "flashear", label: "Flashear por USB", feature: "FIRMWARE_TAB_FLASH_VIEW" },
  { id: "configurar", label: "Conectar o cambiar Wi-Fi", feature: "FIRMWARE_TAB_CONFIG_VIEW" },
  { id: "ota", label: "Actualizar OTA", feature: "FIRMWARE_TAB_OTA_VIEW" },
];

const Firmware = ({ initialTab = "flashear" }) => {
  const { bitset } = usePermisos();
  initialTab = new URLSearchParams(window.location.search).get("fw") || initialTab;
  // Gate principal para mostrar la sección de Firmware dentro de Configuraciones
  const canViewFirmware = has("CONFIGURACIONES_TAB_FIRMWARE_VIEW") ?? true;

  // Filtra pestañas visibles por permisos (si la clave no existe en FEATURE_BITS, queda visible)
  const tabs = useMemo(
    () => ALL_TABS.filter((t) => t.id !== "ota" || isSuperAdmin()).filter((t) => (has(t.feature, bitset) === undefined ? true : has(t.feature, bitset))),
    [bitset]
  );

  // Tab por defecto respetando permisos
  const defaultTab = useMemo(() => {
    if (!canViewFirmware || tabs.length === 0) return null;
    const inList = tabs.some((t) => t.id === initialTab);
    return inList ? initialTab : tabs[0].id;
  }, [canViewFirmware, tabs, initialTab]);

  const [tab, setTab] = useState(defaultTab);
  useEffect(() => setTab(defaultTab), [defaultTab]);

  // Accesibilidad: navegación con flechas entre pestañas
  const tabRefs = useRef({});
  const onKeyDown = (e, idx) => {
    if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) return;
    e.preventDefault();
    const last = tabs.length - 1;
    let nextIdx = idx;

    if (e.key === "ArrowRight") nextIdx = idx === last ? 0 : idx + 1;
    if (e.key === "ArrowLeft") nextIdx = idx === 0 ? last : idx - 1;
    if (e.key === "Home") nextIdx = 0;
    if (e.key === "End") nextIdx = last;

    const nextId = tabs[nextIdx]?.id;
    if (nextId) {
      setTab(nextId);
      tabRefs.current[nextId]?.focus();
    }
  };

  if (!canViewFirmware) {
    return (
      <div className="bg-neutral-900 text-white p-6 rounded-lg">
        <h2 className="text-green-400 text-2xl font-semibold mb-2">Firmware</h2>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4">
          <p className="text-sm text-neutral-300">No tienes permiso para ver Firmware.</p>
        </div>
      </div>
    );
  }

  if (!tab) {
    return (
      <div className="bg-neutral-900 text-white p-6 rounded-lg">
        <h2 className="text-green-400 text-2xl font-semibold mb-2">Firmware</h2>
        <div className="bg-neutral-800 border border-neutral-700 rounded p-4">
          <p className="text-sm text-neutral-300">
            No hay pestañas disponibles con tus permisos actuales.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-neutral-900 text-white p-6 rounded-lg">
      <h2 className="text-green-400 text-2xl font-semibold mb-4">Firmware</h2>

      {/* Pestañas accesibles (sin navegación de rutas, solo estado local) */}
      <div
        role="tablist"
        aria-label="Opciones de firmware"
        className="flex flex-wrap gap-2 sm:gap-3 mb-6"
      >
        {tabs.map((t, idx) => {
          const selected = tab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              ref={(el) => (tabRefs.current[t.id] = el)}
              aria-selected={selected}
              aria-controls={`panel-${t.id}`}
              id={`tab-${t.id}`}
              tabIndex={selected ? 0 : -1}
              onKeyDown={(e) => onKeyDown(e, idx)}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded border transition-colors ${
                selected
                  ? "bg-green-600 border-green-500 text-white"
                  : "bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panel actual */}
      <div
        role="tabpanel"
        id={`panel-${tab}`}
        aria-labelledby={`tab-${tab}`}
        className="bg-neutral-900"
      >
        {tab === "flashear" && <FlashearUSB />}
        {tab === "configurar" && <ConfigurarDispositivo />}
        {tab === "ota" && <ActualizarOTA />}
      </div>
    </div>
  );
};

export default Firmware;
